import { createContext, useContext } from 'react';

/**
 * The minimal user shape the admin template's chrome reads from.
 * Your auth provider may return additional fields — the template ignores them.
 */
export interface AuthUser {
  id: string;
  email: string;
  /** Role string. The template treats 'admin' specially for adminOnly sidebar items. */
  role: string;
  /** Optional display name. Falls back to email when absent. */
  displayName?: string | null;
}

export interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  /** Called by RequireAuth when redirecting to the login route. */
  signIn?: (opts?: { email?: string; password?: string }) => Promise<void>;
  /** Called by the Header's user pill sign-out button. */
  signOut: () => Promise<void> | void;
}

/**
 * Sentinel default — consumers MUST wrap their app in an `<AuthContext.Provider>`.
 * The template calls `useAuth()` and will throw if no provider is mounted.
 */
export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error(
      '[catedge-admin-template] useAuth() was called outside an <AuthContext.Provider>. ' +
        'Wrap your app with an auth provider that supplies { user, loading, signOut } — ' +
        'see examples/SimpleAuthProvider.tsx for a localStorage-backed reference implementation.'
    );
  }
  return value;
}
